// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { Locale } from "@/i18n"

/**
 * Lean barcode identity (Kitchen redesign, 2026-09-14 — ADR-011 reversal;
 * web twin of mobile/src/lib/barcodeLookup.ts, field for field).
 *
 * ADR-011 deferred camera/barcode to "post-pilot," and this pass deliberately
 * does not build the `retail_products` GTIN->ingredient table data-model.md
 * specifies. Instead this resolves a scanned barcode to a product name by
 * calling Open Food Facts directly — the same open database architecture.md
 * already names as the intended future barcode-identity source, and the one
 * services/retail's own price pipeline already talks to — client-side, with
 * no backend involvement. Open Food Facts serves this endpoint with
 * `Access-Control-Allow-Origin: *` (confirmed directly against the live API
 * before choosing this shape), so the browser can call it exactly like the
 * mobile app does.
 *
 * The resolved product becomes a plain natural-language description —
 * "Nutella, 400 g" — fed into the EXISTING extractPantryItems pipeline, the
 * same one free-text quick-add already uses. Nothing new on the backend.
 */

export interface BarcodeProduct {
  code: string
  /** A natural-language description ready to hand to extractPantryItems as sourceText. */
  sourceText: string
  displayName: string
  brand: string | null
  imageUrl: string | null
}

export type BarcodeLookupResult =
  | { found: true; product: BarcodeProduct }
  /** Covers both a genuine "not in the database" response AND any network/parse failure —
   *  the caller's fallback (manual entry) is identical either way. */
  | { found: false }

const OFF_FIELDS = "code,status,product_name,product_name_de,brands,quantity,image_front_small_url"

/** Strips the EU "estimated sign" ("400 g e" -> "400 g") — packaging-law noise, not content. */
function cleanQuantity(raw: string | undefined): string {
  if (!raw) return ""
  return raw.replace(/\s+e\s*$/i, "").trim()
}

export async function lookupBarcode(
  code: string,
  locale: Locale,
  signal?: AbortSignal
): Promise<BarcodeLookupResult> {
  try {
    const res = await fetch(
      `https://world.openfoodfacts.org/api/v2/product/${encodeURIComponent(code)}.json?fields=${OFF_FIELDS}`,
      {
        signal,
        headers: { Accept: "application/json" },
      }
    )
    if (!res.ok) return { found: false }
    const body: {
      status?: number
      product?: {
        product_name?: string
        product_name_de?: string
        brands?: string
        quantity?: string
        image_front_small_url?: string
      }
    } = await res.json()
    if (body.status !== 1 || !body.product) return { found: false }

    const p = body.product
    const name =
      (locale === "de" ? p.product_name_de : p.product_name)?.trim() || p.product_name?.trim()
    if (!name) return { found: false }

    const brand = p.brands?.split(",")[0]?.trim() || null
    const quantity = cleanQuantity(p.quantity)
    const sourceText = [name, quantity].filter(Boolean).join(", ")

    return {
      found: true,
      product: {
        code,
        sourceText,
        displayName: name,
        brand,
        imageUrl: p.image_front_small_url ?? null,
      },
    }
  } catch {
    return { found: false }
  }
}
